import { useHttpContext, useHttpUtil } from 'context/HttpContext';
import { usePermissionContext } from 'context/Permission';
import { useEffect, useState } from 'react';
import { SketchPicker , ColorResult} from 'react-color'
import { useDetailContext } from '../DetailContext';
import ElementTips from '../guide/ElementTips';
import { ActionName, SmartColorFineTuningObject, EventCallbackParamsType } from '../MockupEditor';
import CustomImage from "components/common/CustomImage";


/**
 * 智能换色
 * @param props 属性
 * @param callback 修改属性后的回调
 * @returns 
 */
export default function SmartColor(props: {}){
    const permissionContext = usePermissionContext();
    const httpContent = useHttpContext();
    const httpUtil = useHttpUtil();
    const detailContext = useDetailContext();

    // 属性颜色值是否展开
    const [isColorExpand, setIsColorExpand] = useState<boolean>(true);
    // 其他属性是否展开
    const [isFineTuningExpand, setIsFineTuningExpand] = useState<boolean>(true);

    //  属性面板是否可用
    const [ isPanelEnable, setIsPanelEnable ]= useState<boolean>(true);

    // click时会触发分割服务，是否正在加载
    const [ isLoadingClick, setIsLoadingClick ] = useState<boolean>(false);

    const [ isSaving, setIsSaving ]= useState<boolean>(false);
    
    // 属性
    const [fineTuning, setFineTuning] = useState<SmartColorFineTuningObject>(() => {
        return {
            color: '',  // 颜色值
            opacity: 1, // 透明度
            saturation: 0, // 饱和度
            brightness: 0, // 亮度
            contrast:0  // 对比度
        }
    });
    

    useEffect(() => {
        detailContext?.triggerEditorAction({
            name : ActionName.SMART_COLOR_START,
        })
    },[])

    // 处理编辑器相关的回调
    useEffect(() => {  
        const type = detailContext?.editorCallbackData?.type ;
        const data = detailContext?.editorCallbackData?.data ;
        switch(type){
            case EventCallbackParamsType.SMART_COLOR_AI_READY: // click调用分割服务已完成，就绪
                setIsLoadingClick(false);
                break;
            case EventCallbackParamsType.SMART_COLOR_CHOICE_STATUS: // 智能换色选择状态
                setIsPanelEnable(true);
                setIsFineTuningExpand(true);
                setIsColorExpand(true);
                change();
                break;
            case EventCallbackParamsType.GET_PEN_SIZE_CALLBACK: // 画笔大小
                break;
            case EventCallbackParamsType.ERASE_DRAW_EXIT_CALLBACK: // 画笔退出后的回调
                setIsColorExpand(false);
                setIsFineTuningExpand(false);
                break;
        }
    },[detailContext?.editorCallbackData])
    
    useEffect(() => {  
        const type = detailContext?.editorAction?.name;
        switch(type){
            case ActionName.LOADING_IMAGE:
                cancel();
                break;
        }
    },[detailContext?.editorAction])

    const toggleColorExpand = () => {
        setIsColorExpand(!isColorExpand)
    }

    const toggleFineTuningExpand = () => {
        setIsFineTuningExpand(!isFineTuningExpand)
    }

    // 保存
    const save = async () => {
        if(isLoadingClick) return ;
        if(!fineTuning.color) return httpContent?.response(1, 'Please select the color you want to replace in the right column.');

        const resData = await httpUtil.post('/biz/func/ai/color',{
            projectId: detailContext?.project.projectId
        })
        if(!resData.errCode){
            permissionContext?.updator();
            setIsSaving(true);
            detailContext?.triggerEditorAction({
                name : ActionName.SMART_COLOR_PROCESS,
            })
        }
    }

    // 退出
    const cancel = () => {
        if(isLoadingClick) return ;
        detailContext?.setImageEditType(0);
        detailContext?.triggerEditorAction({
            name : ActionName.VECTOR_AI_ERASE_CLOSE,
        })
    }

    
    const selectedColor = (color: string) => {
        // setIsShowColorPanel(true);
        color && change({...fineTuning, color});
    }

    const changeColor = (color: ColorResult) => {
        // hex hsl hsv rgb
        const {hex} = color ;
        selectedColor(hex);
    }

    const change = (changeData?: SmartColorFineTuningObject) => {
        changeData && setFineTuning(changeData);

        if((changeData || fineTuning).color){
            detailContext?.triggerEditorAction({
                name : ActionName.SMART_COLOR_SETTING,
                params: changeData || fineTuning
            })
        }
    }

    const calcValue = (value: number, step: number, min : number = -1, max : number = 1) => {
        if(step != 0) value = parseFloat((value + step).toFixed(2)) ;
        return Math.max(min, Math.min(max, value))
    }

    const calc = (key: string, step: number) => {
        if(key === 'opacity') return change({...fineTuning, opacity: calcValue(fineTuning.opacity, step, 0)});
        if(key === 'saturation') return change({...fineTuning, saturation: calcValue(fineTuning.saturation, step)});
        if(key === 'brightness') return change({...fineTuning, brightness: calcValue(fineTuning.brightness, step)});
        if(key === 'contrast') return change({...fineTuning, contrast: calcValue(fineTuning.contrast, step)});
    }

    return (
        <>
        {
            <>
                <div className="absolute z-[1201] right-0 top-0 bg-[#FFFFFF]  xl:w-[260px] w-[210px] h-full p-[10px] xl:p-[20px]">
                    <p className="medium-txt font-bold">Smart Color Change</p>
                    <div className="relative flex flex-wrap mt-[10px] xl:mt-[20px] gap-[10px]"
                        style={{
                            color: isPanelEnable ? '#000000' : '#BFBFBF'
                        }}>
                            {
                                !isPanelEnable &&
                                <div className='absolute z-[10] left-0 top-0 w-full h-full cursor-not-allowed'></div>
                            }
                        <div className="w-full">
                            <div className="flex w-full py-[10px] cursor-pointer"
                                onClick={toggleColorExpand}
                                >
                                <span className='font-bold small-txt grow'>Specify color</span>
                                <CustomImage src={isColorExpand ? `/icon/icon-layer-down${isPanelEnable ? '' : '-disabled'}.png` : `/icon/icon-layer-right${isPanelEnable ? '' : '-disabled'}.png`} className='w-[20px] h-[20px]'/>
                            </div>
                            {/* {
                                isColorExpand && 
                                <div className='xl:mt-[10px] w-[80px] h-[80px] rounded-[10px] flex items-center border justify-center cursor-pointer'
                                    style={{
                                        backgroundColor: fineTuning?.color || '#E7E7E7'
                                    }}
                                    onClick={(e) => {e.stopPropagation();selectedColor('');}}>
                                    <CustomImage src='/icon/icon-create-new-white.png' className='w-[21px] h-[21px]' />
                                </div>
                            } */}

                            {
                                isColorExpand && 
                                <div className="mt-[10px] w-full relative">
                                    <SketchPicker color={fineTuning.color} disableAlpha={true} className="rounded-[5px] !shadow-none border xl:!w-[95%] !w-[90%]" 
                                        onChange={(color) => changeColor(color)} />
                                </div>
                            }
                            
                        </div>
                        {
                            <div className="w-full hidden">
                                <div className="flex w-full py-[10px] cursor-pointer"
                                    onClick={toggleFineTuningExpand}>
                                    <span className='font-bold small-txt grow'>Manual fine-tuning</span>
                                    <CustomImage src={isFineTuningExpand ? `/icon/icon-layer-down${isPanelEnable ? '' : '-disabled'}.png` : `/icon/icon-layer-right${isPanelEnable ? '' : '-disabled'}.png`} className='w-[20px] h-[20px]'/>
                                </div>
                                {
                                    isFineTuningExpand && 
                                    <div className=''>
                                        <div className="w-full mt-[10px] flex items-center">
                                            <p className="small-txt basis-1/2 font-semibold">Transparency</p>
                                            <div className="relative text-right basis-1/2">
                                                <CustomImage src="/icon/icon-sub.png" className="absolute w-[20px] h-[20px] right-[58px] top-[3px] cursor-pointer" 
                                                    onClick={() => calc('opacity', -0.05)}/>
                                                <input type="number" className="text-center px-[20px] py-[3px] border border-[#D9D9D9] rounded-[5px] w-[80px]"
                                                    step={0.05} max={1} min={0} value={fineTuning.opacity} 
                                                    onChange={(e) => change({...fineTuning, opacity: calcValue(+e.target.value, 0, 0)})}  />
                                                <CustomImage src="/icon/icon-plus.png" className="absolute w-[20px] h-[20px] right-[2px] top-[3px] cursor-pointer" 
                                                    onClick={() => calc('opacity', 0.05)}/>
                                            </div>
                                        </div>
                                        <div className="w-full mt-[20px] flex">
                                            <p className="small-txt basis-1/2 font-semibold flex items-center">Saturation</p>
                                            <div className="relative text-right basis-1/2">
                                                <CustomImage src="/icon/icon-sub.png" className="absolute w-[20px] h-[20px] right-[58px] top-[3px] cursor-pointer" 
                                                    onClick={() => calc('saturation', -0.05)}/>
                                                <input type="number" className="text-center px-[20px] py-[3px] border border-[#D9D9D9] rounded-[5px] w-[80px]"
                                                    step={0.05} max={1} min={-1} value={fineTuning.saturation} 
                                                    onChange={(e) => change({...fineTuning, saturation: calcValue(+e.target.value, 0, -1)})}  />
                                                <CustomImage src="/icon/icon-plus.png" className="absolute w-[20px] h-[20px] right-[2px] top-[3px] cursor-pointer" 
                                                    onClick={() => calc('saturation', 0.05)}/>
                                            </div>
                                        </div>
                                        <div className="w-full mt-[20px] flex">
                                            <p className="small-txt basis-1/2 font-semibold flex items-center">Contrast</p>
                                            <div className="relative text-right basis-1/2">
                                                <CustomImage src="/icon/icon-sub.png" className="absolute w-[20px] h-[20px] right-[58px] top-[3px] cursor-pointer" 
                                                    onClick={() => calc('contrast', -0.05)}/>
                                                <input type="number" className="text-center px-[20px] py-[3px] border border-[#D9D9D9] rounded-[5px] w-[80px]"
                                                    step={0.05} max={1} min={-1} value={fineTuning.contrast} 
                                                    onChange={(e) => change({...fineTuning, contrast: calcValue(+e.target.value, 0, -1)})}  />
                                                <CustomImage src="/icon/icon-plus.png" className="absolute w-[20px] h-[20px] right-[2px] top-[3px] cursor-pointer" 
                                                    onClick={() => calc('contrast', 0.05)}/>
                                            </div>
                                        </div>
                                        <div className="w-full mt-[20px] flex">
                                            <p className="small-txt basis-1/2 font-semibold flex items-center">Luminance</p>
                                            <div className="relative text-right basis-1/2">
                                                <CustomImage src="/icon/icon-sub.png" className="absolute w-[20px] h-[20px] right-[58px] top-[3px] cursor-pointer" 
                                                    onClick={() => calc('brightness', -0.05)}/>
                                                <input type="number" className="text-center px-[20px] py-[3px] border border-[#D9D9D9] rounded-[5px] w-[80px]"
                                                    step={0.05} max={1} min={-1} value={fineTuning.brightness} 
                                                    onChange={(e) => change({...fineTuning, brightness: calcValue(+e.target.value, 0, -1)})}  />
                                                <CustomImage src="/icon/icon-plus.png" className="absolute w-[20px] h-[20px] right-[2px] top-[3px] cursor-pointer" 
                                                    onClick={() => calc('brightness', 0.05)}/>
                                            </div>
                                        </div>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                </div>

                <div className="absolute bottom-[10px] xl:bottom-[20px] left-[50%] translate-x-[-50%] flex items-center gap-[10px] z-[1202]">
                    <div className="bg-[#D9D9D9] p-[8px] rounded-[10px] cursor-pointer"
                        onClick={cancel}><CustomImage src="/icon/icon-cancel.png" className="w-[26px] h-[26px] xl:w-[34px] xl:h-[34px] "/></div>
                    <div className="bg-[#14A5FA] p-[8px] rounded-[10px] cursor-pointer "
                        onClick={save}>
                        {
                            isSaving ?
                            <p className="animate-spin">
                                <svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4287" className="w-[26px] h-[26px] xl:w-[34px] xl:h-[34px]" ><path d="M96 512c0-19.33 15.67-35 35-35s35 15.67 35 35c0 191.09 154.91 346 346 346s346-154.91 346-346-154.91-346-346-346c-19.33 0-35-15.67-35-35s15.67-35 35-35c229.75 0 416 186.25 416 416S741.75 928 512 928 96 741.75 96 512z" fill="#10F28D" p-id="4288"></path></svg>
                            </p>
                            :
                            <CustomImage src="/icon/icon-confirm.png" className="w-[26px] h-[26px] xl:w-[34px] xl:h-[34px]"/>
                        }
                    </div>
                </div>
            </>
        }

            <ElementTips />
        </>
    )
}